<template>
  <SnowLocationPageContent
    :location="location"
    selected-tab-title="Snow Summary"
  >
    <SnowForecastSummaryCard
      class="tw-mb-4 lg:tw-mb-8"
      :location="location"
    />
    <MultiModelPlumeSummary
      class="tw-mb-4 lg:tw-mb-8"
      :location="location"
      :is-snow-view="true"
    />
    <ResortSponsorshipCard
      v-if="resortSponsorship"
      class="tw-mb-4 lg:tw-mb-8"
      :resort-sponsorship="resortSponsorship"
    />
    <div :class="dailySnowAndWeatherClass">
      <LocationDailySnowSummaryCard
        v-if="dailySnow"
        class="md:tw-items-stretch tw-mb-4 md:tw-mb-8"
        :card-class-names="cardClassNames"
        :daily-snow="dailySnow"
      />
      <CurrentWeatherCard
        class="md:tw-items-stretch tw-mb-4 md:tw-mb-8"
        :card-class-names="currentWeatherCardClassNames"
        :is-stretched="true"
        :location="location"
        :narrow-view="hasDailySnow"
      />
    </div>
    <Snow5DayHourlyForecastCard
      class="tw-mb-4 lg:tw-mb-8"
      :location="location"
      @fetch-history-snow-detail="handleFetchHistorySnowDetail"
    />
    <Snow10DaySemiDailyForecastCard
      class="tw-mb-4 lg:tw-mb-8"
      :location="location"
      @fetch-history-snow-detail="handleFetchHistorySnowDetail"
    />
    <MoreInfoCard
      class="tw-mb-4 lg:tw-mb-8"
      :location="location"
      :show-attribution-detail="false"
      view-type="forecast"
    />
  </SnowLocationPageContent>
</template>

<script>
import { mapActions } from 'pinia';
import { parseOpenMountainApiError } from '@@/utils/CommonUtils';
import { useLocationStore } from '@@/stores/Location';
import LocationPageMixin, { getSetupParams } from '@@/components/Location/Mixins/LocationPageMixin';

export default {
  name: 'LocationSnowSummaryPage',

  mixins: [LocationPageMixin],

  async setup() {
    const locationStore = useLocationStore();
    const params = getSetupParams();

    const { data, error } = await useAsyncData(() => locationStore.fetchForecastSnowDetail(params));

    if (error.value) {
      throw createError(parseOpenMountainApiError(error.value));
    }

    const location = ref(data.value);

    return { location };
  },

  computed: {
    cardClassNames() {
      return this.hasDailySnow ? this.$style.cardBodyStretch : null;
    },

    currentWeatherCardClassNames() {
      if (this.hasDailySnow) {
        return `${this.$style.cardBodyStretch} tw-flex tw-flex-col tw-justify-evenly`;
      }

      return null;
    },

    dailySnow() {
      return this?.location?.daily_read;
    },

    dailySnowAndWeatherClass() {
      if (this.hasDailySnow) {
        return 'md:tw-grid md:tw-grid-cols-2 md:tw-gap-x-5';
      }

      return null;
    },

    hasDailySnow() {
      return !!this.dailySnow;
    },

    resortSponsorship() {
      return this.location?.resort_sponsorship;
    },
  },

  methods: {
    ...mapActions(useLocationStore, ['fetchForecastSnowDetail', 'fetchHistorySnowDetail']),

    async fetchLocation(params = {}) {
      const location = await this.fetchForecastSnowDetail(params);

      if (this.location.history_hourly || this.location.history_semi_daily) {
        const {
          history_hourly,
          history_semi_daily,
        } = await this.fetchHistorySnowDetail(params);

        location.history_hourly = history_hourly;
        location.history_semi_daily = history_semi_daily;
      }

      return location;
    },

    async handleFetchHistorySnowDetail() {
      /* eslint camelcase: off */
      const { is_custom } = this.location;
      const { slug } = this.$route.params;
      const { units } = this;

      try {
        const {
          history_hourly,
          history_semi_daily,
        } = await this.fetchHistorySnowDetail({ is_custom, slug, units });

        this.location.history_hourly = history_hourly;
        this.location.history_semi_daily = history_semi_daily;
      }
      catch (e) {
        this.$toast.open({ message: 'Unable to fetch snow history!', type: 'error' });
      }
    },
  },
};
</script>

<style module>
@media (min-width: 768px) {
  .cardBodyStretch {
    height: calc(100% - 3.75rem);
  }
}
</style>
